import {
    keepPreviousData,
    useInfiniteQuery,
    useQueries,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import {
    fetchPatientsCount,
    fetchPatientsList,
    type PatientsCountQueryParams,
} from '~/backend/connector-next-api/connector-next-components';
import {
    queryKeyFn,
    useConnectorNextContext,
} from '~/backend/connector-next-api/connector-next-context';
import type { PatientSummaryDto } from '~/backend/connector-next-api/connector-next-schemas';
import {
    IMPORT_PROGRESS_POLL_MS,
    patientFilterQueryParams,
    patientImportActivity,
    sortDirectionParam,
    sortKeyParam,
    type PatientFilters,
    type Sort,
} from '../model';

/**
 * connector-next caps a page at 200 patients (PatientListQuery.MaxPageSize) and
 * answers 400 above it, so reaching the rest of a larger cohort means walking
 * the pages.
 */
const PAGE_SIZE = 200;

/** The key every loaded patient list is cached under, whatever its search, sort and filters. */
export const PATIENT_LIST_QUERY_KEY = ['api', 'patients', 'list'];

type PatientListQuery = {
    /** Free-text patient search, or undefined for the whole cohort. */
    search: string | undefined;
    sort: Sort;
    /** Server-side filters; an unfiltered dimension is simply not sent. */
    filters: PatientFilters;
    /** False holds the query back, for a list whose parameters are not known yet. */
    enabled?: boolean;
};

export type PatientListResult = {
    /** Every patient loaded so far, in the server's order. */
    patients: PatientSummaryDto[];
    /** Patients matching the query server-side — not the number loaded. */
    totalCount: number;
    hasMore: boolean;
    loadMore: () => void;
    isLoadingMore: boolean;
    /** No page has loaded yet, also while the query is held back. */
    isLoading: boolean;
    /**
     * A search/sort change is in flight over an already-rendered list — not the
     * first load, and not a next page, both of which have their own indicator.
     */
    isRefreshing: boolean;
    /** The rows shown are a previous query's, kept until this query's own arrive. */
    isPlaceholderData: boolean;
    isError: boolean;
    error: unknown;
};

/**
 * The patient list, walked one page at a time. Search, sort and filters reset
 * the walk; loadMore appends the next page to what is already shown.
 */
export function usePatientList({
    search,
    sort,
    filters,
    enabled = true,
}: PatientListQuery): PatientListResult {
    const { fetcherOptions, queryOptions } = useConnectorNextContext();

    const query = useInfiniteQuery({
        queryKey: [...PATIENT_LIST_QUERY_KEY, { search, sort, filters }],
        queryFn: ({ pageParam, signal }) =>
            fetchPatientsList(
                {
                    ...fetcherOptions,
                    queryParams: {
                        page: pageParam,
                        pageSize: PAGE_SIZE,
                        search,
                        sort: sortKeyParam(sort.key),
                        direction: sortDirectionParam(sort.direction),
                        ...patientFilterQueryParams(filters),
                    },
                },
                signal
            ),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            // An empty page ends the walk even if totalCount disagrees, so a
            // miscount cannot spin the list forever.
            if ((lastPage.items?.length ?? 0) === 0) {
                return undefined;
            }
            const loaded = allPages.reduce(
                (count, page) => count + (page.items?.length ?? 0),
                0
            );
            return loaded < (lastPage.totalCount ?? 0)
                ? allPages.length + 1
                : undefined;
        },
        // Keep the previous list mounted across sort/search so it doesn't blank
        // to a spinner; a lightweight overlay signals the background refetch.
        placeholderData: keepPreviousData,
        // A row with series still arriving changes without anyone acting, so the
        // list re-reads itself until every loaded row has settled.
        refetchInterval: (current) =>
            current.state.data?.pages.some((page) =>
                page.items?.some(
                    (patient) => patientImportActivity(patient).inFlight > 0
                )
            )
                ? IMPORT_PROGRESS_POLL_MS
                : false,
        ...queryOptions,
        enabled,
    });

    const { fetchNextPage } = query;
    const isFetching = query.isFetching;
    const loadMore = useCallback(() => {
        // Any in-flight fetch — a next page or a search/sort refresh over the
        // placeholder-kept list — shares the query's single fetch, so firing
        // another would cancel or overwrite it.
        if (isFetching) {
            return;
        }
        void fetchNextPage();
    }, [fetchNextPage, isFetching]);

    const pages = query.data?.pages;
    const patients = useMemo(
        () => (pages ?? []).flatMap((page) => page.items ?? []),
        [pages]
    );

    return {
        patients,
        // The latest page's count is the one getNextPageParam trusts; the UI
        // must agree or the footer can claim patients are missing after the
        // walk ends.
        totalCount: pages?.[pages.length - 1]?.totalCount ?? patients.length,
        hasMore: query.hasNextPage,
        loadMore,
        isLoadingMore: query.isFetchingNextPage,
        isLoading: query.isPending,
        isRefreshing:
            query.isFetching && !query.isLoading && !query.isFetchingNextPage,
        isPlaceholderData: query.isPlaceholderData,
        isError: query.isError,
        error: query.error,
    };
}

/**
 * Every patient a query matches: walks the pages until none is left, and
 * reports loading until the walk is complete, so a list derived from it never
 * shows a partial cohort as if it were whole. A query held back never walks.
 */
export function useWholePatientList(
    query: PatientListQuery
): PatientListResult {
    const list = usePatientList(query);
    const { hasMore, isError, loadMore } = list;
    const walking = query.enabled !== false && hasMore && !isError;
    // loadMore changes identity when a fetch settles, which re-runs this for
    // the next page.
    useEffect(() => {
        if (walking) {
            loadMore();
        }
    }, [walking, loadMore]);
    // The previous query's rows are another cohort, however many pages they hold.
    return {
        ...list,
        isLoading:
            list.isLoading || list.isPlaceholderData || (hasMore && !isError),
    };
}

/**
 * How many patients each named query matches, server-side over the whole
 * cohort, under the name it was asked by; undefined while a count is unknown
 * and for a query given as undefined, which is not asked. The connector counts
 * without building the list's rows.
 */
export function usePatientCounts<K extends string>(
    queries: readonly (readonly [K, PatientsCountQueryParams | undefined])[]
): ReadonlyMap<K, number | undefined> {
    const { fetcherOptions, queryOptions } = useConnectorNextContext();
    const results = useQueries({
        queries: queries.map(([, queryParams]) => ({
            queryKey: queryKeyFn({
                path: '/api/patients/count',
                operationId: 'patientsCount',
                variables: { queryParams },
            }),
            queryFn: ({ signal }: { signal: AbortSignal }) =>
                fetchPatientsCount({ ...fetcherOptions, queryParams }, signal),
            ...queryOptions,
            enabled: queryParams !== undefined,
        })),
    });
    return new Map(
        queries.map(([key], index) => [key, results[index]?.data?.totalCount])
    );
}
